import React from "react";
import { getTimeFromNow } from "../../../../utils/time";
import { Student } from "./types";
import { useT } from "../../../../stores/uiLang";
import { TD_T } from "../../../../i18n/teacherDashboard";
import { displayEmail } from "../../../../utils/kidLogin";

interface StudentProgressRowProps {
  student: Student;
}

export function StudentProgressRow({ student }: StudentProgressRowProps) {
  const t = useT(TD_T);
  const getStatusColor = (status: string) => {
    switch (status) {
      case "Active":
        return "bg-green-100 text-green-800";
      case "Below Average":
        return "bg-yellow-100 text-yellow-800";
      case "Needs Attention":
        return "bg-red-100 text-red-800";
      case "No assignments":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatLastLogin = (lastLogin: string | null): string => {
    if (!lastLogin) return t.never;
    return getTimeFromNow(lastLogin);
  };

  const formatLastSubmitted = (lastSubmitted: string | null): string => {
    if (!lastSubmitted) return "-";
    return getTimeFromNow(lastSubmitted);
  };

  return (
    <tr className="hover:bg-gray-50 cursor-pointer">
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          <div className="flex-shrink-0 h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
            <span className="text-sm font-medium text-indigo-800">
              {student.fullname
                .split(" ")
                .map((n) => n[0])
                .join("")
                .toUpperCase()}
            </span>
          </div>
          <div className="ml-4">
            <div className="text-sm font-medium text-gray-900">
              {student.fullname}
            </div>
            <div className="text-sm text-gray-500">{displayEmail(student.email, "—")}</div>
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        <span className="text-sm text-gray-900">{student.className}</span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className="text-sm text-gray-900">
          {student.averageGrade >= 0
            ? student.averageGrade.toFixed(1) + "%"
            : "-"}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className="text-sm text-gray-900">
          {student.assignmentName || "-"}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        <span className="text-sm text-gray-900">
          {formatLastSubmitted(student.lastSubmitted)}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {formatLastLogin(student.lastLogin)}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span
          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(
            student.status
          )}`}
        >
          {t.perfLabels[student.status] ?? student.status}
        </span>
      </td>
    </tr>
  );
}
